import React, { useEffect, useState } from 'react';
import { FaComments, FaTimes, FaRocketchat } from 'react-icons/fa';
import ChatAutomation from './ChatAutomation';

interface LiveChatProps {
  websiteId?: string;
  className?: string;
}

declare global {
  interface Window {
    $crisp: any;
    CRISP_WEBSITE_ID: string;
  }
}

const LiveChat: React.FC<LiveChatProps> = ({ 
  websiteId = '9f7b8c12-3d4e-5f6a-7b8c-9d0e1f2a3b4c', // Default demo ID - replace with your actual Crisp website ID
  className = '' 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showWidget, setShowWidget] = useState(true);

  useEffect(() => {
    // Set the website ID
    window.CRISP_WEBSITE_ID = websiteId;

    // Create the script element
    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;

    // Handle script load
    script.onload = () => {
      setIsLoaded(true);
      
      // Configure Crisp when it loads
      if (window.$crisp) {
        // Hide the default crisp chat button
        window.$crisp.push(['do', 'chat:hide']);
        
        // Set up event listeners
        window.$crisp.push(['on', 'chat:opened', () => setIsOpen(true)]);
        window.$crisp.push(['on', 'chat:closed', () => setIsOpen(false)]);
        
        // Customize appearance
        window.$crisp.push(['set', 'chat:color', ['purple']]);
        window.$crisp.push(['set', 'user:company', 'Farsi Learner']);
        
        // Set up welcome message
        window.$crisp.push(['set', 'message:text', ['Hello! 👋 Welcome to Farsi Learner!\n\nI\'m here to help you with your Persian learning journey. How can I assist you today?']]);
        
        // Configure automated responses
        setupAutomatedResponses();
      }
    };

    // Add script to document
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      
      // Reset Crisp
      if (window.$crisp) {
        window.$crisp = [];
      }
    };
  }, [websiteId]);

  const setupAutomatedResponses = () => {
    if (!window.$crisp) return;
    
    // Set up quick replies for common questions
    window.$crisp.push(['set', 'session:data', {
      'segments': ['farsi-learner'],
      'custom': {
        'learning_level': 'beginner',
        'app_version': '1.0',
        'platform': 'web'
      }
    }]);

    // Set up automated messages based on user behavior
    setTimeout(() => {
      if (window.$crisp) {
        window.$crisp.push(['do', 'message:send', [
          'text',
          '🎯 **Quick Help Options:**\n\n' +
          '• Type "audio" for audio troubleshooting\n' +
          '• Type "lessons" for lesson guidance\n' +
          '• Type "alphabet" for alphabet help\n' +
          '• Type "subscription" for billing questions\n' +
          '• Type "human" to speak with our team\n\n' +
          'What would you like help with? 😊'
        ]]);
      }
    }, 2000);
  };

  const openChat = () => {
    if (window.$crisp && isLoaded) {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
      setIsOpen(true);
    }
  };

  const closeChat = () => {
    if (window.$crisp && isLoaded) {
      window.$crisp.push(['do', 'chat:close']);
      setIsOpen(false);
    }
  };

  const hideWidget = () => {
    setShowWidget(false);
  };

  if (!showWidget) {
    return null;
  }

  return (
    <>
      {/* Chat Automation Component */}
      <ChatAutomation websiteId={websiteId} />
      
      {/* Custom Chat Button */}
      <div className={`fixed bottom-6 right-6 z-50 ${className}`}>
        {!isOpen && (
          <div className="relative">
            {/* Chat Button */}
            <button
              onClick={openChat}
              disabled={!isLoaded}
              className={`group relative bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 ${
                !isLoaded ? 'opacity-50 cursor-not-allowed' : 'animate-pulse hover:animate-none'
              }`}
              title="Start Live Chat"
            >
              <FaComments className="text-2xl" />
              
              {/* Pulse rings for attention */}
              <div className="absolute inset-0 rounded-full bg-purple-400 opacity-75 animate-ping"></div>
              <div className="absolute inset-0 rounded-full bg-purple-400 opacity-50 animate-ping" style={{ animationDelay: '0.5s' }}></div>
            </button>

            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-3 whitespace-nowrap">
                💬 Start Live Chat
                <div className="absolute top-full right-4 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>

            {/* Close widget button */}
            <button
              onClick={hideWidget}
              className="absolute -top-1 -left-1 bg-gray-600 hover:bg-gray-700 text-white rounded-full p-1 text-xs transition-colors duration-200"
              title="Hide chat widget"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        )}

        {/* Chat Status Indicator */}
        {isOpen && (
          <div className="bg-green-500 text-white rounded-full p-3 shadow-lg">
            <FaRocketchat className="text-xl" />
          </div>
        )}
      </div>

      {/* Welcome Message Popup */}
      {isLoaded && !isOpen && (
        <div className="fixed bottom-24 right-6 max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-purple-200 dark:border-purple-800/30 p-4 z-40 animate-fade-in">
          <div className="flex items-start space-x-3">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-full p-2 flex-shrink-0">
              <FaComments className="text-white text-sm" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                Need help? 👋
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">
                Start a conversation with us! We're here to help with your Farsi learning journey.
              </p>
              <button
                onClick={openChat}
                className="mt-2 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
              >
                Start Chat →
              </button>
            </div>
            <button
              onClick={hideWidget}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* Custom styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default LiveChat;
